import PropTypes from 'prop-types'
import { useCallback, useMemo, useState } from 'react'
import theme from 'theme/theme'
import { NoteForm } from '@components/note'
import { AdasBox, AdasButton, AdasPaper, AdasTabs, AdasTypography } from '@components/wrapper-components'

const NOTE_TYPES = {
  EXTERNAL: 'external',
  INTERNAL: 'internal',
  CLIENT: 'client_note'
}

const NOTE_CONFIG = {
  [NOTE_TYPES.EXTERNAL]: {
    label: 'External Note',
    maxLength: 1000,
    getLabel: () => 'Notes Printed on Invoice'
  },
  [NOTE_TYPES.INTERNAL]: {
    label: 'Internal NOTE',
    maxLength: 500,
    getLabel: () => 'Notes for Technician'
  },
  [NOTE_TYPES.CLIENT]: {
    label: 'CLIENT NOTE',
    maxLength: 1000,
    getLabel: (clientRebate) => (clientRebate > 0 ? `Client Rebate: ${clientRebate}%` : '')
  }
}

export const Note = ({
  submit,
  existingInternalNote,
  existingExternalNote,
  existingClientNote,
  allowEdit = true,
  noBottomBorder = false,
  clientRebate
}) => {
  const [noteType, setNoteType] = useState(NOTE_TYPES.EXTERNAL)
  const [isAddNote, setIsAddNote] = useState(false)

  const currentNote = useMemo(() => {
    switch (noteType) {
      case NOTE_TYPES.INTERNAL:
        return existingInternalNote
      case NOTE_TYPES.CLIENT:
        return existingClientNote
      default:
        return existingExternalNote
    }
  }, [noteType, existingExternalNote, existingInternalNote, existingClientNote])

  const currentConfig = NOTE_CONFIG[noteType]
  const maxLength = currentConfig.maxLength

  const tabs = Object.entries(NOTE_CONFIG).map(([value, { label }]) => ({
    value,
    label
  }))

  const getNoteLabel = (noteType, clientRebate) => {
    switch (noteType) {
      case 'external':
        return 'Notes Printed on Invoice'
      case 'internal':
        return 'Notes for Technician'
      case 'client_note':
        return clientRebate > 0 ? `Client Rebate: ${clientRebate}%` : 'Client Note'
      default:
        return ''
    }
  }
  const label = getNoteLabel(noteType, clientRebate)

  const handleSubmit = useCallback(
    (value) => {
      const noteData = {
        notes_external: noteType === NOTE_TYPES.EXTERNAL ? value : existingExternalNote,
        notes_internal: noteType === NOTE_TYPES.INTERNAL ? value : existingInternalNote,
        client_notes: noteType === NOTE_TYPES.CLIENT ? value : existingClientNote
      }

      submit({ note: noteData, noteType })
      setIsAddNote(false)
    },
    [noteType, existingExternalNote, existingInternalNote, existingClientNote, submit]
  )

  const handleTabChange = useCallback((e, value) => {
    setNoteType(value)
    setIsAddNote(false)
  }, [])

  const showEmptyState = !currentNote && !isAddNote

  return (
    <AdasBox sx={{ borderBottom: noBottomBorder ? 0 : 1, borderColor: 'divider' }}>
      <AdasTabs tabs={tabs} value={noteType} onChange={handleTabChange} aria-label='note listing tab' />
      <AdasPaper
        variant='outlined'
        sx={{
          marginBottom: '20px',
          backgroundColor: theme.palette.background.light
        }}
      >
        {showEmptyState ? (
          <AdasBox sx={{ height: '112px', margin: '20px', display: 'flex' }}>
            <AdasTypography sx={{ margin: 'auto', textAlign: 'center' }} variant='body2'>
              No Note Added
              <AdasButton
                sx={{ display: 'block', marginTop: '20px' }}
                variant='outlined'
                aria-label='add note'
                color='primary'
                onClick={() => setIsAddNote(true)}
                disabled={!allowEdit}
              >
                Add Note
              </AdasButton>
            </AdasTypography>
          </AdasBox>
        ) : (
          <NoteForm
            noteType={noteType}
            existingNote={currentNote}
            cancel={() => setIsAddNote(false)}
            submit={handleSubmit}
            label={label}
            allowEdit={allowEdit}
            maxLength={maxLength}
            txAreaBgColor='white'
          />
        )}
      </AdasPaper>
    </AdasBox>
  )
}

Note.propTypes = {
  submit: PropTypes.func.isRequired,
  existingInternalNote: PropTypes.string,
  existingExternalNote: PropTypes.string,
  existingClientNote: PropTypes.string,
  allowEdit: PropTypes.bool,
  noBottomBorder: PropTypes.bool,
  clientRebate: PropTypes.number
}
